import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getUserEmails,
    getUserEditDataByEmail,
    getEntitiesByType,
    replaceRolesAndEntities,
    getUserData,
    uploadUserEntitiesByClubLookup
} from '../../../redux/slices/userSlice';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import EditUserModal from './EditUserModal'
import UploadUserEntitiesModal from './UploadUserEntitiesModal';
import './edit-user.scss';


const EditUser = () => {
    const { user } = useSelector((state) => state.user);
    const isUKUser = user?.CountryID === 2;
    // email pattern to check againt
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // debounce time to limit db pings
    const DEBOUNCE_MS = 500;

    // upload list modal
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);

    // dispatch for server calls
    const dispatch = useDispatch();

    // Initialize the state variable 'isToggled' to false
    const [isAddToggled, setIsAddToggled] = useState(true);

    // default US
    const [countryIdFilter, setCountryIdFilter] = useState(1);

    // const for holding full email list
    const userEmails = useSelector(state => state.user.userEmails);

    // identiier for entities for add/unadd/remove
    const entityKey = (e) => {
        const id =
            e?.EntityType === 2 ? e.EntityID :
                e?.EntityType === 3 ? e.ChainID :
                    e?.ClubID;

        return `${e?.EntityType}:${id}`;
    };

    // data for edit user dialog
    // tree structure to hold networks/chains/clubs
    const [entityTree, setEntityTree] = useState([]);
    // array of roles for user
    const [updatedRoles, setUpdatedRoles] = useState([]);

    // user edit data (basic user info, roles, entities)
    const editUserData = useSelector(state => state.user.editUserData);
    const editUserDataStatus = useSelector(state => state.user.editUserDataStatus);
    const editUserDataError = useSelector(state => state.user.editUserDataError);

    // entity data for edit user screen
    const entityData = useSelector(state => state.user.entityData);
    const entityDataStatus = useSelector(state => state.user.entityDataStatus);
    const entityDataError = useSelector(state => state.user.entityDataError);

    //search for entity data
    const [searchEntityName, setSearchEntityName] = useState('');
    const [searchEntityID, setSearchEntityID] = useState('');
    //debounce the search for entity data
    const [debouncedSearchEntityName, setDebouncedSearchEntityName] = useState('');
    const [debouncedSearchEntityID, setDebouncedSearchEntityID] = useState('');

    // tadded entity data will go here for comparison
    const [addedEntities, setAddedEntities] = useState([]);
    // added entities by key
    const addedKeys = useMemo(() => {
        return new Set(addedEntities.map(entityKey));
    }, [addedEntities]);
    // identifiere for if an entity is present in added list
    const isEntityAdded = (e) => addedKeys.has(entityKey(e));

    // userID for getEntitiesByType dispatch
    const [userId, setUserId] = useState(null);

    // initialize forms and error states
    const [searchEmail, setSearchEmail] = useState('');
    const [errors, setErrors] = useState({});

    // dialog forms
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [showEditDialog, setShowEditDialog] = useState(false);

    // btn group on edit dialog for adding entities
    const [isNetworksActive, setNetworksActive] = useState(false);
    const [isChainsActive, setChainsActive] = useState(false);
    const [isClubsActive, setClubsActive] = useState(true);

    // handler for uploading comma separated list on clubid/zoom site code/customer key
    const handleUploadSubmit = async ({ lookupType, valuesCsv }) => {
        if (!userId) return false;

        setUploading(true);
        try {
            const action = await dispatch(uploadUserEntitiesByClubLookup({
                userId,
                lookupType,
                valuesCsv
            }));

            if (!uploadUserEntitiesByClubLookup.fulfilled.match(action)) {
                console.error('Upload failed:', action.payload || action.error);
                return false;
            }

            // close the small upload modal
            setShowUploadModal(false);

            // close the edit dialog
            const emailToEdit = (selectedEmail ?? searchEmail)?.trim();
            closeEditDialog();

            // re-pull edit data and reopen
            const refreshed = await dispatch(getUserEditDataByEmail(emailToEdit));
            if (getUserEditDataByEmail.fulfilled.match(refreshed)) {
                const dto = refreshed.payload;
                // reset state + baseline from fresh server truth
                hydrateEditDialogFromDto(dto, emailToEdit);
                // reopen edit dialog
                openEditDialog(emailToEdit);
            }

            return false;
        } finally {
            setUploading(false);
        }
    };

    // helpers to rehydrate dialog after a list upload, used in handleConfirm
    const normalizeEmail = (e) => (e ?? '').trim().toLowerCase();
    const assignedEntityKey = (e) => `${e?.EntityType}:${findCorrectKey(e)}`;
    const hydrateEditDialogFromDto = (dto, emailToEdit) => {
        // ui state
        setUserId(dto.UserID);
        setSelectedEmail(emailToEdit); 
        setUpdatedRoles(dto.RoleIds ?? []);
        setEntityTree(dto.Entities ?? []);
        setAddedEntities([]);

        // baseline snapshot for isDirty
        baselineRef.current = {
            email: normalizeEmail(emailToEdit),
            roles: [...(dto.RoleIds ?? [])],
            entityKeys: new Set((dto.Entities ?? []).map(assignedEntityKey)),
        };
    };

    // handler for btn group
    const handleBtnGroupChange = async (e) => {

        // clear entity searches if present and grab clean list
        setSearchEntityName('');
        setSearchEntityID('');

        if (e === 'network') {
            setNetworksActive(true);
            setChainsActive(false);
            setClubsActive(false);
            // grab list of networks
            dispatch(getEntitiesByType({
                userId: userId,
                entityType: 'network',
                searchLike: null,
                searchId: null,
                countryId: countryIdFilter,
                maxResults: 250}));
        } else if (e === 'chain') {
            setNetworksActive(false);
            setChainsActive(true);
            setClubsActive(false);
            // grab list of chains
            dispatch(getEntitiesByType({
                userId: userId,
                entityType: 'chain',
                searchLike: null,
                searchId: null,
                countryId: countryIdFilter,
                maxResults: 250
            }));
        } else {
            setNetworksActive(false);
            setChainsActive(false);
            setClubsActive(true);
            // grab list of clubs
            dispatch(getEntitiesByType({
                userId: userId,
                entityType: 'club',
                searchLike: null,
                searchId: null,
                countryId: countryIdFilter,
                maxResults: 250
            }));
        }
    };

    // dialog open/close
    const openEditDialog = (email) => {

        // set email (for follow up server call) and open dialog
        setSelectedEmail(email);
        setShowEditDialog(true);
    };
    const closeEditDialog = () => {

        // reset all variables on dialog close
        setSearchEntityName('');
        setSearchEntityID('');

        setNetworksActive(true);
        setChainsActive(false);
        setClubsActive(false);

        setIsAddToggled(true);
        setShowEditDialog(false);
        setSelectedEmail(null);
        setSearchEmail('');
        setCountryIdFilter(1);
    };

    // flip the boolean value of add btn
    const toggleAddHandler = () => {
        setIsAddToggled(prev => !prev);
    };

    // filter emailList for UI search 
    const filteredEmails = useMemo(() => {
        const search = searchEmail.trim().toLowerCase();
        if (!Array.isArray(userEmails)) return [];

        // if empty search, return nothing
        if (!search) return [];

        // ui searchLike
        return userEmails
            .filter(e => e?.toLowerCase().includes(search))
            .sort((a, b) => {
                const aLower = a.toLowerCase();
                const bLower = b.toLowerCase();

                // prefix matches first 
                const aStarts = aLower.startsWith(search);
                const bStarts = bLower.startsWith(search);
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;

                return a.localeCompare(b);
            });
    }, [userEmails, searchEmail]);

    // watcher for hasResults, boolean to disable/enable edit user btn
    const hasResults = useMemo(() => {
        const email = searchEmail.trim();
        return emailPattern.test(email) && filteredEmails.length > 0;
    }, [searchEmail, filteredEmails]);

    // handler method for when a user inputs into the 'Search for User by Email' input
    const handleSearchChange = async(e) => {
        setSearchEmail(e);
    };

    // when a user chooses to edit, open the dialog with the correct data objects retrieved from 
    // server side, i.e. roles, basic info and entities
    const handleConfirm = async(e) => {
        e.preventDefault();
        setErrors({});

        const emailToEdit = (selectedEmail ?? searchEmail)?.trim();

        // fetch DTO from server
        const action = await dispatch(getUserEditDataByEmail(emailToEdit));

        if (getUserEditDataByEmail.fulfilled.match(action)) {
            // populate uid to allow entity list fetch on first render
            const dto = action.payload;

            hydrateEditDialogFromDto(dto, emailToEdit);
            openEditDialog(emailToEdit);
        } else {
            console.error('Failed to fetch edit user data');
        }
    };

    // retrieve the correct key for UI display and mapping purposes
    // for the edit user entity table/scrollable
    const findCorrectKey = (e) => {
        if (e?.EntityType === 2) return e.EntityID;
        if (e?.EntityType === 3) return e.ChainID;
        return e.ClubID;
    };

    // helper method for dirty check on save button
    const arrayEqualAsSets = (a = [], b = []) => {
        if (a.length !== b.length) return false;
        const sa = [...a].sort((x, y) => x - y);
        const sb = [...b].sort((x, y) => x - y);
        for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
        return true;
    };
    const baselineRef = useRef({
        email: '',
        roles: [],
        entityKeys: new Set(),
    });
    // end helper  


    // const for save enable/disable in dialog
    const isDirty = useMemo(() => {
        // email dirty
        const emailDirty = normalizeEmail(selectedEmail) !== baselineRef.current.email;

        // roles dirty
        const rolesDirty = !arrayEqualAsSets(updatedRoles, baselineRef.current.roles);

        // entities dirty
        const currentKeys = new Set((entityTree ?? []).map(assignedEntityKey));
        const baseKeys = baselineRef.current.entityKeys;

        let entitiesDirty = currentKeys.size !== baseKeys.size;
        if (!entitiesDirty) {
            for (const k of currentKeys) {
                if (!baseKeys.has(k)) {
                    entitiesDirty = true;
                    break;
                }
            }
        }

        return emailDirty || rolesDirty || entitiesDirty;
    }, [selectedEmail, updatedRoles, entityTree]);

    // function to mutate network tree depending on level effected (add)
    const handleAdd = (entity) => {
        if (!entity) return;

        const key = entityKey(entity);

        // 1 track as newly added for icon + for save payload later
        setAddedEntities((prev) => {
            if (prev.some((x) => entityKey(x) === key)) return prev;
            return [...prev, entity];
        });

        // 2 put it into the “current” entityTree list i.e. what the user will see as assigned
        setEntityTree((prev) => {
            if (prev.some((x) => entityKey(x) === key)) return prev;
            return [...prev, entity];
        });
    };
    // function to mutate network tree depending on level effected (remove)
    const handleRemove = (entity) => {
        if (!entity) return;

        const key = entityKey(entity);

        setAddedEntities((prev) => prev.filter((x) => entityKey(x) !== key));
        setEntityTree((prev) => prev.filter((x) => entityKey(x) !== key));
    };

    // save the user changes
    const handleDialogConfirm = async(e) => {
        // build list of entities to send to server
        const entitiesToAdd = [];
        entityTree?.map((entity) => {
            entitiesToAdd.push({ entityType: entity.EntityType, entityId: findCorrectKey(entity) })
        });
        const entitiesCsv = entitiesToAdd.map(e => `${e.entityType}:${e.entityId}`).join(',');

        // grab roles
        const rolesCsv = updatedRoles.join(',');

        // server call
        const action = await dispatch(replaceRolesAndEntities({
            userId: userId,
            newEmail: selectedEmail,
            rolesCsv,
            entitiesCsv
        }));

        // refresh list if save worked
        if (replaceRolesAndEntities.fulfilled.match(action)) {
            await dispatch(getUserData());
            await dispatch(getUserEmails());
        } else {
            console.error('Save failed:', action.payload || action.error);
            return;
        }

        // close dialog and reset variables
        setSearchEntityName('');
        setSearchEntityID('');

        setNetworksActive(true);
        setChainsActive(false);
        setClubsActive(false);

        setIsAddToggled(true);
        setShowEditDialog(false);
        setSelectedEmail(null);
        setSearchEmail('');
    };

    // grab email list and initial entity lists to search on
    useEffect(() => {
        dispatch(getUserEmails());
        if (userId === null) {
            return;
        } else {
            dispatch(getEntitiesByType({
                userId: userId,
                entityType: 'club',
                searchLike: null,
                searchId: null,
                countryId: countryIdFilter,
                maxResults: 250
            }));
        }
    }, [dispatch, userId]);

    // use effects for debounced search against DB
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchEntityName(searchEntityName);
        }, DEBOUNCE_MS);

        return () => clearTimeout(handler);
    }, [searchEntityName]);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchEntityID(searchEntityID);
        }, DEBOUNCE_MS);

        return () => clearTimeout(handler);
    }, [searchEntityID]);

    // effect for lookups on name and id for entities 
    useEffect(() => {
        // prevent bad network call on initial load
        if (userId === null) {
            return;
        } else {
            let entityTypeStr = '';
            if (isNetworksActive) entityTypeStr = 'network';
            if (isChainsActive) entityTypeStr = 'chain';
            if (isClubsActive) entityTypeStr = 'club';

            dispatch(getEntitiesByType({
                userId: userId,
                entityType: entityTypeStr,
                searchLike: debouncedSearchEntityName,
                searchId: +debouncedSearchEntityID,
                countryId: countryIdFilter,
                maxResults: 250
            }));
        }
    }, [debouncedSearchEntityName, debouncedSearchEntityID, countryIdFilter]);

    // markup template
    return (
        <>
            <div className="container">
                <form onSubmit={handleConfirm}>
                    {/* space to match other blocks when search is empty */}
                    {filteredEmails.length === 0 && <div className="email-search-btn-margin-top"></div>}
                    <label>Search for User by Email</label>
                    {/* email */}
                    <div className="form-outline mb-4">
                        <input
                            className="form-control form-control-lg"
                            id="form1Example1"
                            onChange={(e) => handleSearchChange(e.target.value)}
                            type="text"
                            value={searchEmail}
                        />
                    </div>
                    {/* space to match other blocks when search is empty */}
                    {filteredEmails.length === 0 && <div className="email-search-btn-margin-bot"></div>}
                    {filteredEmails.length > 0 &&
                        <div className="list-container"> {filteredEmails.length } Users Found:
                            <ul className="scroll-list form-outline">
                                {filteredEmails.map((item) => (
                                    <li className="email-list-item"
                                        key={item}
                                        onClick={() => {
                                            setSearchEmail(item);
                                            setSelectedEmail(item);
                                        }}>
                                        <FontAwesomeIcon icon={faUser} className="list-icon" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    }
                    {/* submit for edit user */}
                    <div className="w-100 mt-4">
                        <Button
                            className="btn-lg w-100"
                            type="submit"
                            variant="primary"
                            disabled={!hasResults}
                        >
                            Edit User
                        </Button>
                    </div>
                </form>
            </div>

            {/* EDIT USER DIALOG CONTENT */}
            <EditUserModal
                show={showEditDialog}
                onClose={closeEditDialog}
                editUserData={editUserData}

                status={editUserDataStatus}
                error={editUserDataError}
                selectedEmail={selectedEmail}
                onSelectedEmailChange={setSelectedEmail}

                updatedRoles={updatedRoles}
                onRolesChange={setUpdatedRoles}

                entityTree={entityTree}
                onRemoveEntity={handleRemove}

                isAddToggled={isAddToggled}
                onToggleAddMode={toggleAddHandler}
                isNetworksActive={isNetworksActive}
                isChainsActive={isChainsActive}
                isClubsActive={isClubsActive}
                handleBtnGroupChange={handleBtnGroupChange}

                entityAddData={entityData}
                entitySearchLike={searchEntityName}
                onEntitySearchLikeChange={setSearchEntityName}
                entityIDSearch={searchEntityID}
                onEntityIDSearchChange={setSearchEntityID}

                countryIdFilter={countryIdFilter}
                onCountryIdFilterChange={setCountryIdFilter}

                addedEntities={addedEntities}
                onAddEntity={handleAdd}
                entityKey={entityKey}
                isEntityAdded={isEntityAdded}

                onOpenUploadModal={() => setShowUploadModal(true)}

                onSave={handleDialogConfirm}
                isDirty={isDirty}

                isUKUser={isUKUser}
            />
            <UploadUserEntitiesModal
                show={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onSubmit={handleUploadSubmit}
                isSubmitting={uploading}
            />
        </>
    );
};

export default EditUser;